import { isBoolean, toInteger } from 'lodash';
import { trackConfig, type IFramework } from '@/framework';
import { scaleConfig } from '@/layers/brush-layer';
import { Layer } from '@/layers/layer';
import { convertBrushDataToVectorData } from '@/utils/layer-kit';
import { LayerType, type BrushData, type ILayerProps, type IVectorLayerProps } from '@/version-types';
import { type VectorSVGMaterial } from '../types';
import { createDefaultVectorLayerData, createVectorLayer, pushHistory, updateLayer } from '../utils';

export interface IShortcut {
    /**
     * 通过画笔数据添加矢量图层
     * @param data - 画笔数据
     * @returns
     */
    addByBrushData(data: BrushData, options?: { byMousePosition?: boolean }): ILayerProps;

    /**
     * 通过矢量材质添加矢量图层
     * @param material - 矢量素材
     * @param options - 可选参数
     *  `options.byMousePosition` - 是否根据鼠标位置添加，默认为 false
     *  `options.keepOriginSize` - 是否保持素材原始尺寸，默认为 false
     *  `options.trackFunc` - 埋点标识，默认为空
     * @returns
     */
    addByVectorMaterial(
        material: VectorSVGMaterial,
        options?: { byMousePosition?: boolean; keepOriginSize?: boolean; trackFunc?: string },
    ): ILayerProps;

    /**
     * 根据矢量图层素材替换图片图层为矢量图层
     * @param imageLayer - 图片图层
     * @param material - 矢量素材
     * @returns
     */
    replaceImageLayerToVectorLayer(imageLayer: ILayerProps, material: VectorSVGMaterial): ILayerProps;
}

const isBrushData = (source: BrushData | VectorSVGMaterial): source is BrushData => 'brushType' in source;

export class Shortcut implements IShortcut {
    constructor(
        private framework: IFramework,
        private baseLayer: Layer,
    ) {}

    private normalizeVectorLayerData(source: BrushData | VectorSVGMaterial) {
        const stageIdx = this.framework.stage.getActiveStageIndex();
        const { width: stageWidth } = this.framework.getSizeByIndex(stageIdx);

        if (isBrushData(source)) {
            const scaledBrushData = scaleConfig(source, stageWidth) as BrushData;
            const vectorLayerData = convertBrushDataToVectorData(scaledBrushData) as IVectorLayerProps;
            return vectorLayerData;
        }

        const { url, info } = source;
        const layerData = createDefaultVectorLayerData({ ...info, url });

        // 防止素材描边宽度为 0 时，置空描边颜色
        if (layerData.strokeWidth === 0) {
            layerData.stroke = '';
            layerData.strokeGradient = null;
        }

        return layerData;
    }

    private createLayer(source: BrushData | VectorSVGMaterial, options = { keepOriginSize: false }) {
        const { keepOriginSize } = options;

        const layerData = this.normalizeVectorLayerData(source);
        const { naturalWidth, naturalHeight } = layerData;

        const stageIdx = this.framework.stage.getActiveStageIndex();
        const { width: stageWidth, height: stageHeight } = this.framework.getSizeByIndex(stageIdx);

        let scaleSize = 2;
        let materialId = '';

        if (isBrushData(source)) {
            scaleSize = 3;
        } else {
            materialId = String(source.id);
        }

        const scaleHeightRotate = naturalWidth / naturalHeight;

        const centerPoint = {
            x: toInteger(stageWidth / 2),
            y: toInteger(stageHeight / 2),
        };

        const stageRatio = stageWidth / stageHeight;
        const ratio = naturalWidth / naturalHeight;

        let width = 0;
        let height = 0;

        if (stageRatio > ratio) {
            height = toInteger(stageHeight / scaleSize);
            width = toInteger(naturalWidth / (naturalHeight / height));
        } else {
            width = toInteger(stageWidth / scaleSize);
            height = toInteger(naturalHeight / (naturalWidth / width));
        }

        if (scaleHeightRotate !== 1) {
            height = toInteger(width / scaleHeightRotate);
        }

        if (!isBrushData(source) && keepOriginSize) {
            width = naturalWidth;
            height = naturalHeight;
        }

        let lockRatio = true;
        if (!isBrushData(source) && isBoolean(source.lockRatio)) {
            lockRatio = source.lockRatio;
        }

        const layer = createVectorLayer({
            width,
            height,
            left: toInteger(centerPoint.x - width / 2),
            top: toInteger(centerPoint.y - height / 2),
            materialId,
            data: layerData,
            lockRatio,
        });

        return layer;
    }

    addByBrushData(data: BrushData, options = { byMousePosition: false }) {
        const { byMousePosition } = options;

        let layer = this.createLayer(data);
        layer = byMousePosition
            ? this.baseLayer.addByMousePosition({
                  layer,
                  trackFunc: trackConfig[LayerType.VECTOR_LAYER].add,
              })
            : this.baseLayer.addByDiagonal({
                  layer,
                  trackFunc: trackConfig[LayerType.VECTOR_LAYER].add,
              });

        return layer;
    }

    addByVectorMaterial(
        material: VectorSVGMaterial,
        options = { byMousePosition: false, keepOriginSize: false, trackFunc: '' },
    ) {
        const { byMousePosition, keepOriginSize, trackFunc } = options;

        let layer = this.createLayer(material, { keepOriginSize });

        const payload = {
            layer,
            trackFunc: trackFunc || trackConfig[LayerType.VECTOR_LAYER].add,
        };

        layer = byMousePosition ? this.baseLayer.addByMousePosition(payload) : this.baseLayer.addByDiagonal(payload);

        return layer;
    }

    replaceImageLayerToVectorLayer(imageLayer: ILayerProps, material: VectorSVGMaterial) {
        const vectorLayer = this.createLayer(material);

        vectorLayer.id = imageLayer.id;
        vectorLayer.left = imageLayer.left;
        vectorLayer.top = imageLayer.top;
        vectorLayer.parentId = imageLayer.parentId;

        updateLayer(this.framework, vectorLayer);
        pushHistory(this.framework, imageLayer, vectorLayer);

        return vectorLayer;
    }
}
